import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Stack,
  useTheme,
  Link,
  IconButton,
  TextField,
  Button,
  Grid
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const CookiePolicy = () => {
  const theme = useTheme();

  const sections = [
    {
      title: "What Are Cookies",
      content: [
        "Cookies are small text files that are placed on your device when you visit a website.",
        "They are widely used to make websites work more efficiently and provide information to website owners.",
        "Cookies can be 'persistent' or 'session' cookies. Persistent cookies remain on your device after you close your browser, while session cookies are deleted when you close your browser.",
        "Cookies do not contain any personal information that can identify you individually."
      ]
    },
    {
      title: "How We Use Cookies",
      content: [
        "We use cookies for several purposes, including:",
        "• Essential cookies: Required for the website to function properly",
        "• Analytics cookies: Help us understand how visitors use our website",
        "• Functionality cookies: Remember your preferences and settings",
        "• Performance cookies: Help us improve website performance",
        "• Marketing cookies: Used for advertising and marketing purposes"
      ]
    },
    {
      title: "Types of Cookies We Use",
      content: [
        "Essential Cookies: These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you such as setting your privacy preferences, logging in, or filling in forms.",
        "Analytics Cookies: We use Google Analytics and similar services to understand how visitors interact with our website. This helps us improve our content and user experience.",
        "Functionality Cookies: These cookies allow the website to remember choices you make and provide enhanced, more personal features.",
        "Performance Cookies: These cookies help us measure and improve the performance of our website by collecting information about how visitors use it."
      ]
    },
    {
      title: "Third-Party Cookies",
      content: [
        "We may use third-party services that place cookies on your device:",
        "• Google Analytics: For website analytics and performance monitoring",
        "• Social Media Platforms: Facebook, Twitter, and other social media cookies when you interact with social media features",
        "• Advertising Networks: For displaying relevant advertisements",
        "• Content Delivery Networks: For faster content delivery",
        "These third-party services have their own privacy policies and cookie practices."
      ]
    },
    {
      title: "Cookie Management",
      content: [
        "You can control and manage cookies in several ways:",
        "Browser Settings: Most browsers allow you to refuse cookies or delete them. You can usually find these settings in the 'options' or 'preferences' menu of your browser.",
        "Cookie Consent: When you first visit our website, you can choose which types of cookies to accept.",
        "Opt-out Tools: You can use browser extensions or tools to block certain types of cookies.",
        "Please note that disabling certain cookies may affect the functionality of our website."
      ]
    },
    {
      title: "Your Cookie Choices",
      content: [
        "When you visit our website for the first time, you will see a cookie consent banner that allows you to:",
        "• Accept all cookies",
        "• Accept only essential cookies",
        "• Customize your cookie preferences",
        "• Learn more about our cookie policy",
        "You can change your cookie preferences at any time through your browser settings."
      ]
    },
    {
      title: "Cookie Retention",
      content: [
        "The length of time cookies remain on your device depends on their type:",
        "Session Cookies: These are temporary and are deleted when you close your browser.",
        "Persistent Cookies: These remain on your device for a set period or until you delete them manually.",
        "Analytics Cookies: Typically remain for 1-2 years unless you clear them.",
        "Marketing Cookies: May remain for up to 2 years or until you opt out."
      ]
    },
    {
      title: "Data Collected by Cookies",
      content: [
        "The information collected by cookies may include:",
        "• Your IP address and general location",
        "• Browser type and version",
        "• Operating system",
        "• Pages visited and time spent on each page",
        "• Links clicked and navigation patterns",
        "• Device information and screen resolution",
        "• Language preferences",
        "This information is used to improve our website and provide a better user experience."
      ]
    },
    {
      title: "International Transfers",
      content: [
        "Some of our third-party cookie providers may transfer data to countries outside Ethiopia.",
        "We ensure that any international transfers comply with applicable data protection laws.",
        "Third-party services like Google Analytics may process data in the United States or other countries.",
        "By accepting cookies, you consent to such international transfers of data."
      ]
    },
    {
      title: "Updates to This Policy",
      content: [
        "We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons.",
        "We will notify you of any material changes by posting the updated policy on our website.",
        "Your continued use of our website after any changes constitutes acceptance of the updated policy.",
        "We encourage you to review this policy periodically to stay informed about how we use cookies."
      ]
    }
  ];

  return (
    <>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 6,
            position: 'relative'
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                align="center"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Cookie Policy
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  opacity: 0.9,
                  maxWidth: '800px',
                  mx: 'auto'
                }}
              >
                Learn how we use cookies to improve your browsing experience
              </Typography>
            </motion.div>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                This Cookie Policy explains how EthioReview uses cookies and similar technologies to recognize you when you visit our website. 
                It explains what these technologies are and why we use them, as well as your rights to control our use of them.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Last updated:</strong> {new Date().toLocaleDateString()}
              </Typography>
            </Paper>
          </motion.div>

          {/* Policy Sections */}
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main', 
                    mb: 3,
                    fontSize: { xs: '1.5rem', md: '1.75rem' }
                  }}
                >
                  {section.title}
                </Typography>
                <Stack spacing={2}>
                  {section.content.map((item, itemIndex) => (
                    <Typography 
                      key={itemIndex} 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.7,
                        pl: item.startsWith('•') ? 0 : 2,
                        borderLeft: item.startsWith('•') ? 'none' : `3px solid ${theme.palette.primary.light}`,
                        ml: item.startsWith('•') ? 2 : 1
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            </motion.div>
          ))}

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Contact Us
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
                If you have any questions about our use of cookies, please contact us:
              </Typography>
              <Stack spacing={2} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">cookies@ethioreview.com</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">123 News Street, Addis Ababa, Ethiopia</Typography>
                </Box>
              </Stack>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quick Links
              </Typography>
              <Stack spacing={1}>
                {[
                  { name: "Home", path: "/" },
                  { name: "About Us", path: "/about" },
                  { name: "Contact", path: "/contact" }
                ].map((item) => (
                  <motion.div whileHover={{ x: 5 }} key={item.name}>
                    <Link
                      to={item.path}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Categories */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Categories
              </Typography>
              <Stack spacing={1}>
                {["Politics", "Business", "Sports"].map((category) => (
                  <motion.div whileHover={{ x: 5 }} key={category}>
                    <Link
                      to={`/${category.toLowerCase()}-news`}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {category}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Contact Info */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    123 News Street, Addis Ababa, Ethiopia
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">info@ethioreview.com</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Social Media & Newsletter */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                  { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                  { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                  { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                  { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                ].map((social) => (
                  <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                    <IconButton
                      href="#"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        backgroundColor: `${social.color}10`,
                        color: social.color,
                        "&:hover": { backgroundColor: `${social.color}20` },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Newsletter
              </Typography>
              <Box component="form" sx={{ display: "flex" }}>
                <TextField
                  size="small"
                  placeholder="Your email"
                  variant="outlined"
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright & Legal */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} EthioReview. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Cookie Policy", path: "/cookies" }
              ].map((item) => (
                <Typography
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default CookiePolicy; 